import React, { useState, useEffect, useRef, useCallback } from 'react';
import PacientSearchSelect from '../../PacientSearchSelect/PacientSearchSelect';
import { fetchBookings, updateBooking } from '../../api';
import {
  FaCalendarAlt,
  FaCheck,
  FaChevronDown,
  FaClock,
  FaExclamationCircle,
  FaInfoCircle,
  FaList,
  FaRegCalendarAlt,
  FaSearch,
  FaSyringe,
  FaTable,
  FaUserMd
} from 'react-icons/fa';
import EditBookingModal from '../Modals/EditBookingModal';
import { useAuth } from '../../../hooks/useAuth';
import { USER_GROUPS } from '../../../config/permissions';
import { parseCurrencyValue } from '../../../utils/currency';

const sortBookingsByDate = (bookingsToSort) => {
  return [...bookingsToSort].sort((a, b) => {
    if (!a.datetimeAtendimento) return 1;
    if (!b.datetimeAtendimento) return -1;
    return new Date(a.datetimeAtendimento) - new Date(b.datetimeAtendimento);
  });
};

const normalizeProceduresForEdit = (booking) => {
  if (Array.isArray(booking.procedimentosList) && booking.procedimentosList.length > 0) {
    return booking.procedimentosList;
  }

  if (!Array.isArray(booking.procedimentos)) {
    return [];
  }

  return booking.procedimentos
    .map((proc) => {
      if (typeof proc === 'string') {
        return { nome: proc };
      }

      return {
        ...proc,
        nome: proc?.nome || proc?.name || ''
      };
    })
    .filter((proc) => proc.nome);
};

const getStatusTimestampField = (status) => {
  if (status === 'Chegou') {
    return 'status_chegou_at';
  }

  if (status === 'Realizado') {
    return 'status_realizado_at';
  }

  return null;
};

const formatStatusTimestamp = (value) => {
  if (!value) {
    return '';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }

  const pad = (part) => String(part).padStart(2, '0');

  return [
    `${pad(parsed.getDate())}/${pad(parsed.getMonth() + 1)}/${pad(parsed.getFullYear()).slice(-2)}`,
    `${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`
  ].join(' ');
};

const getStatusHoverText = (booking, statusOverride) => {
  const status = statusOverride || booking?.status || 'Agendado';
  const timestampField = getStatusTimestampField(status);
  if (!timestampField) {
    return '';
  }

  const formatted = formatStatusTimestamp(booking?.[timestampField]);
  return formatted || '';
};

function ViewBookings({ professionals, rooms }) {
  const [bookings, setBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [viewMode, setViewMode] = useState('table');
  const [searchProfessional, setSearchProfessional] = useState(null);
  const [searchPatient, setSearchPatient] = useState(null);
  const [searchDate, setSearchDate] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState(['Realizado', 'Agendado', "Chegou", "Confirmado", "Bloqueio"]);
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const statusDropdownRef = useRef(null);
  const statusOptions = ['Realizado', 'Agendado', 'Chegou', 'Confirmado', 'Cancelado', "Bloqueio"];
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [saveFeedback, setSaveFeedback] = useState(null);
  const { userGroups } = useAuth();
  const isProfissional = userGroups.includes(USER_GROUPS.PROFISSIONAL);

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    setSearchDate(today);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(e.target)) {
        setIsStatusDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadBookings = useCallback(async (queryParams = {}) => {
    try {
      setBookingsLoading(true);
      const bookingsData = await fetchBookings(queryParams);
      setBookings(sortBookingsByDate(bookingsData));
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setBookingsLoading(false);
    }
  }, []);

  useEffect(() => {
    const queryParams = {};
    if (searchDate) {
      queryParams.date = searchDate;
    }
    loadBookings(queryParams);
  }, [searchDate, loadBookings]);

  const handleEditBooking = (booking) => {
    if (isProfissional) return;
    const procedimentosList = normalizeProceduresForEdit(booking);
    setSelectedBooking(booking);
    setEditFormData({
      atendimento_id: booking.atendimento_id,
      patientId: booking.patientId,
      pacientId: booking.pacientId,
      paciente: booking.paciente,
      title: booking.title,
      dataAtendimento: booking.datetimeAtendimento,
      duration: booking.duration,
      procedimentos: booking.procedimentos,
      procedimentosList,
      procedimentosDirty: false,
      status: booking.status || 'Agendado',
      status_chegou_at: booking.status_chegou_at || '',
      status_realizado_at: booking.status_realizado_at || '',
      observacao: booking.observacao,
      financeiro_observacao: booking.financeiro_observacao,
      valorTotal: booking.valorTotal,
      paymentMethod: booking.paymentMethod || '',
      paymentStatus: booking.paymentStatus || '',
      paymentDate: booking.paymentDate || booking.datetimeAtendimento,
      payment_installments: booking.payment_installments,
      profissional: booking.profissional,
      file_key: booking.file_key,
      booking_agent: booking.booking_agent,
      booking_channel: booking.booking_channel || ''
    });
    setShowEditModal(true);
  };

  const matchesCurrentSearch = (booking) => {
    if (searchProfessional && booking.profissional !== searchProfessional.id) {
      return false;
    }

    if (searchPatient) {
      const bookingPatientId = booking.patientId || booking.pacientId;
      if (bookingPatientId !== searchPatient.pacientId) {
        return false;
      }
    }

    if (searchDate && (booking.datetimeAtendimento || '').slice(0, 10) !== searchDate) {
      return false;
    }

    return true;
  };

  const matchesLoadedDateScope = (booking) => {
    return !searchDate || (booking.datetimeAtendimento || '').slice(0, 10) === searchDate;
  };

  const getBookingFromUpdateResponse = (response) => {
    return response?.booking || response?.updatedBooking || response?.data?.booking || null;
  };

  const addStatusTimestampToUpdates = (sourceBooking, updates) => {
    const nextStatus = updates.status || 'Agendado';
    const previousStatus = sourceBooking?.status || 'Agendado';

    if (nextStatus === 'Chegou' && previousStatus !== 'Chegou' && previousStatus !== 'Realizado') {
      return {
        ...updates,
        status_chegou_at: new Date().toISOString()
      };
    }

    if (nextStatus === 'Realizado' && previousStatus !== 'Realizado') {
      return {
        ...updates,
        status_realizado_at: new Date().toISOString()
      };
    }

    return updates;
  };

  const buildLocalBookingUpdate = (sourceFormData, updates, response) => {
    const serverBooking = getBookingFromUpdateResponse(response);
    const procedimentosList = updates.procedimentosList || sourceFormData.procedimentosList || [];
    const baseBooking = selectedBooking?.atendimento_id === updates.atendimento_id
      ? selectedBooking
      : {
        atendimento_id: updates.atendimento_id,
        patientId: sourceFormData.patientId,
        pacientId: sourceFormData.pacientId || sourceFormData.patientId,
        paciente: sourceFormData.paciente,
        title: sourceFormData.title,
      };

    return {
      ...baseBooking,
      ...(serverBooking || {}),
      atendimento_id: updates.atendimento_id,
      datetimeAtendimento: serverBooking?.datetimeAtendimento || sourceFormData.dataAtendimento,
      dataAtendimento: serverBooking?.dataAtendimento || sourceFormData.dataAtendimento,
      duration: sourceFormData.duration,
      procedimentos: Array.isArray(procedimentosList) ? procedimentosList : baseBooking.procedimentos,
      procedimentosList: Array.isArray(procedimentosList) ? procedimentosList : baseBooking.procedimentosList,
      status: updates.status,
      status_chegou_at: serverBooking?.status_chegou_at || updates.status_chegou_at || baseBooking.status_chegou_at || '',
      status_realizado_at: serverBooking?.status_realizado_at || updates.status_realizado_at || baseBooking.status_realizado_at || '',
      observacao: sourceFormData.observacao,
      financeiro_observacao: sourceFormData.financeiro_observacao,
      valorTotal: updates.valorTotal,
      paymentMethod: sourceFormData.paymentMethod || '',
      paymentStatus: sourceFormData.paymentStatus || '',
      paymentDate: sourceFormData.paymentDate,
      payment_installments: sourceFormData.payment_installments,
      profissional: updates.professionalId,
      file_key: serverBooking?.file_key || sourceFormData.file_key || baseBooking.file_key,
      file_name: serverBooking?.file_name || sourceFormData.file_name || baseBooking.file_name,
      booking_agent: sourceFormData.booking_agent,
      booking_channel: sourceFormData.booking_channel || ''
    };
  };

  const applyBookingUpdateLocally = (updatedBooking, status, bookingId) => {
    setSelectedBooking(updatedBooking);
    setBookings((currentBookings) => {
      if (status === 'Deletado') {
        return currentBookings.filter(booking => booking.atendimento_id !== bookingId);
      }

      const shouldRemainLoaded = matchesLoadedDateScope(updatedBooking);
      const existingIndex = currentBookings.findIndex(booking => booking.atendimento_id === bookingId);

      if (!shouldRemainLoaded) {
        return currentBookings.filter(booking => booking.atendimento_id !== bookingId);
      }

      if (existingIndex === -1) {
        return sortBookingsByDate([...currentBookings, updatedBooking]);
      }

      return sortBookingsByDate(
        currentBookings.map(booking => (
          booking.atendimento_id === bookingId ? updatedBooking : booking
        ))
      );
    });
  };

  const handleUpdateBooking = async (status, formDataOverride, options = {}) => {
    const sourceFormData = formDataOverride || editFormData;
    const shouldOptimisticallyClose = !options.keepModalOpen && status !== 'Deletado';
    const requestedStatus = status || sourceFormData?.status;
    const bookingId = sourceFormData?.atendimento_id;

    if (!bookingId) {
      throw new Error('Agendamento sem identificador para atualizar.');
    }

    if (
      requestedStatus === 'Cancelado'
      && (selectedBooking?.status || 'Agendado') !== 'Cancelado'
      && !window.confirm('Cancelar este agendamento? Esta acao remove o evento do Google Calendar.')
    ) {
      return;
    }

    if (shouldOptimisticallyClose) {
      setShowEditModal(false);
      setSaveFeedback({ status: 'loading', message: 'Salvando agendamento...' });
    }

    setIsLoading(true);
    try {
      // Extract professionalId from formData.profissional, and exclude formData.profissional
      // to avoid sending both fields to the API
      const { atendimento_id, patientId, pacientId, paciente, profissional, proofSaved, procedimentosDirty, ...rawRestFormData } = sourceFormData;
      const restFormData = { ...rawRestFormData };
      if (proofSaved) {
        delete restFormData.file;
      }

      const procedimentosList = Array.isArray(restFormData.procedimentosList)
        ? restFormData.procedimentosList.map(proc => ({
          ...proc,
          valor: parseCurrencyValue(proc.valor)
        }))
        : [];
      const shouldSendProcedures = procedimentosDirty || procedimentosList.length > 0;
      
      const updates = {
        atendimento_id: bookingId,
        ...restFormData,
        valorTotal: parseCurrencyValue(restFormData.valorTotal),
        professionalId: profissional, // Use the profissional value as professionalId
        status: status || restFormData.status
      };

      if (shouldSendProcedures) {
        updates.procedimentosList = procedimentosList;
      } else {
        delete updates.procedimentosList;
        delete updates.procedimentos;
      }
      
      const updatesWithStatusTimestamp = addStatusTimestampToUpdates(selectedBooking, updates);
      const updateResponse = await updateBooking(updatesWithStatusTimestamp);
      const updatedBooking = buildLocalBookingUpdate(sourceFormData, updatesWithStatusTimestamp, updateResponse);
      applyBookingUpdateLocally(updatedBooking, status, bookingId);

      if (options.keepModalOpen) {
        setEditFormData({
          ...sourceFormData,
          proofSaved: Boolean(sourceFormData.file_name || sourceFormData.file_key)
        });
      } else if (!shouldOptimisticallyClose) {
        setShowEditModal(false);
      } else {
        setSaveFeedback({ status: 'success', message: 'Agendamento salvo com sucesso' });
      }
    } catch (error) {
      console.error('Error updating booking:', error);
      if (shouldOptimisticallyClose) {
        setEditFormData(sourceFormData);
        setShowEditModal(true);
        setSaveFeedback({ status: 'error', message: 'Falha na edicao' });
      }
      if (!shouldOptimisticallyClose || options.keepModalOpen) {
        throw error;
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!saveFeedback || saveFeedback.status === 'loading') return undefined;

    const timeout = setTimeout(() => {
      setSaveFeedback(null);
    }, saveFeedback.status === 'success' ? 2600 : 4000);

    return () => clearTimeout(timeout);
  }, [saveFeedback]);

  const renderSaveFeedback = () => {
    if (!saveFeedback) return null;

    return (
      <div className={`booking-save-feedback ${saveFeedback.status}`} role="status" aria-live="polite">
        <div className="booking-save-feedback-icon">
          {saveFeedback.status === 'loading' && <span className="booking-save-feedback-spinner" aria-hidden="true" />}
          {saveFeedback.status === 'success' && <FaCheck />}
          {saveFeedback.status === 'error' && <FaExclamationCircle />}
        </div>
        <div>
          <strong>
            {saveFeedback.status === 'loading' && 'Salvando'}
            {saveFeedback.status === 'success' && 'Salvo'}
            {saveFeedback.status === 'error' && 'Erro'}
          </strong>
          <span>{saveFeedback.message}</span>
        </div>
      </div>
    );
  };

  const renderBookingsList = () => {
    // Find professional name by ID
    const getProfessionalName = (professionalId) => {
      const professional = professionals.find(p => p.id === professionalId);
      return professional ? professional.name : professionalId;
    };

    const filteredBookings = bookings.filter(booking =>
      matchesCurrentSearch(booking) && selectedStatuses.includes(booking.status || 'Agendado')
    );

    const getInitials = (name = '') => {
      return name
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part[0])
        .join('')
        .toUpperCase() || '--';
    };

    const getStatusClass = (status = 'Agendado') => {
      return status
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-');
    };

    return (
      <div className="bookings-list view-bookings-area">
        {/* <h3>Agendamentos</h3> */}
        <div className="view-toggle-buttons">
          <button 
            className={`view-toggle-button ${viewMode === 'table' ? 'active' : ''}`}
            onClick={() => setViewMode('table')}
          >
            <FaTable /> Tabela
          </button>
          <button 
            className={`view-toggle-button ${viewMode === 'calendar' ? 'active' : ''}`}
            onClick={() => setViewMode('calendar')}
          >
            <FaRegCalendarAlt /> Calendário
          </button>
        </div>
        
        {filteredBookings.length === 0 ? (
          <div className="bookings-empty-state">
            <FaCalendarAlt />
            <p>Nenhum agendamento encontrado.</p>
          </div>
        ) : viewMode === 'table' ? (
          <div className="bookings-table-shell">
            <table className="bookings-table">
              <thead>
                <tr>
                  <th>Paciente</th>
                  <th>Profissional</th>
                {/* <th>Data</th> */}
                  <th>Horário</th>
                  <th>Procedimentos</th>
                  <th>Responsável pelo agendamento</th>
                  <th>Status</th>
                  <th>Observação</th>
                  <th>Calendário</th>
                </tr>
              </thead>
              <tbody>
                {filteredBookings.map((booking, index) => {
                  const timeOnly = booking.datetimeAtendimento ? 
                    booking.datetimeAtendimento.split('T')[1].slice(0, 5) : 
                    'N/A';
                  const professionalName = getProfessionalName(booking.profissional);
                  const bookingStatus = booking.status || 'Agendado';
                  const statusHoverText = getStatusHoverText(booking, bookingStatus);

                  return (
                    <tr 
                      key={booking.atendimento_id || index}
                      className={`booking-table-row status-${getStatusClass(bookingStatus)} ${isProfissional ? 'read-only' : ''}`}
                      onClick={!isProfissional ? () => handleEditBooking(booking) : undefined}
                    >
                      <td>
                        <div className="booking-person-cell">
                          <span className="booking-avatar patient">{getInitials(booking.paciente)}</span>
                          <span className="booking-primary-text">{booking.paciente}</span>
                        </div>
                      </td>
                      <td>
                        <div className="booking-person-cell">
                          <span className="booking-avatar professional">{getInitials(professionalName)}</span>
                          <span className="booking-primary-text">{professionalName}</span>
                        </div>
                      </td>
                    {/* <td>{booking.dataAtendimento}</td> */}
                      <td>
                        <div className="booking-time-cell">
                          <FaClock />
                          <span>{timeOnly}</span>
                        </div>
                      </td>
                      <td>
                        <div className="booking-procedure-list">
                          {(booking.procedimentos || []).map((proc, i) => (
                            <span className="booking-procedure-chip" key={i}>
                              <FaSyringe /> {proc.nome}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td>
                        {booking.booking_agent ? (
                          <div className="booking-person-cell compact">
                            <span className="booking-avatar agent">{getInitials(booking.booking_agent)}</span>
                            <span className="booking-primary-text">{booking.booking_agent}</span>
                          </div>
                        ) : (
                          <span className="booking-muted">--</span>
                        )}
                      </td>
                      <td>
                        <span
                          className={`booking-status-badge status-${getStatusClass(bookingStatus)}`}
                          title={statusHoverText || undefined}
                        >
                          {bookingStatus === 'Realizado' && <FaCheck />}
                          {bookingStatus}
                          {statusHoverText && (
                            <FaInfoCircle
                              className="booking-status-info-icon"
                              aria-label="Passe o mouse para ver o horário"
                            />
                          )}
                        </span>
                      </td>
                      <td>
                        <span className="booking-observation">
                          {booking.observacao || '--'}
                        </span>
                      </td>
                      <td onClick={(e) => {
                        e.stopPropagation();
                        booking.googleEventUrl && window.open(booking.googleEventUrl, '_blank');
                      }}>
                        {booking.googleEventUrl ? (
                          <button type="button" className="booking-calendar-button" aria-label="Abrir evento no calendário">
                            <FaCalendarAlt />
                          </button>
                        ) : (
                          <span className="booking-muted">--</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="bookings-table-footer">
              Mostrando {filteredBookings.length} de {bookings.length} agendamentos
            </div>
          </div>
        ) : (
          <div className="calendar-view">
            {renderCalendarView(filteredBookings)}
          </div>
        )}
      </div>
    );
  };

  const renderCalendarView = (bookings) => {
    // Group bookings by date and professional
    const bookingsByDate = {};
    
    bookings.forEach(booking => {
      if (!booking.datetimeAtendimento) return;
      
      const dateStr = booking.dataAtendimento;
      const professionalId = booking.profissional;
      
      if (!bookingsByDate[dateStr]) {
        bookingsByDate[dateStr] = {};
      }
      
      if (!bookingsByDate[dateStr][professionalId]) {
        bookingsByDate[dateStr][professionalId] = [];
      }
      
      bookingsByDate[dateStr][professionalId].push(booking);
    });

    // Return calendar view for the current date
    const dates = Object.keys(bookingsByDate).sort();
    
    if (dates.length === 0) {
      return <p>Nenhum agendamento encontrado.</p>;
    }

    // Helper function to detect if two appointments overlap
    const isOverlapping = (appt1, appt2) => {
      // Get start and end times
      const startTime1 = new Date(appt1.datetimeAtendimento).getTime();
      const endTime1 = startTime1 + (appt1.duration * 60 * 1000);
      
      const startTime2 = new Date(appt2.datetimeAtendimento).getTime();
      const endTime2 = startTime2 + (appt2.duration * 60 * 1000);
      
      // Check if they overlap
      return (startTime1 < endTime2 && startTime2 < endTime1);
    };

    // Helper function to group overlapping appointments
    const groupOverlappingAppointments = (appointments) => {
      if (!appointments.length) return [];
      
      // Sort appointments by start time
      const sorted = [...appointments].sort((a, b) => 
        new Date(a.datetimeAtendimento).getTime() - new Date(b.datetimeAtendimento).getTime()
      );
      
      const groups = [];
      let currentGroup = [sorted[0]];
      
      for (let i = 1; i < sorted.length; i++) {
        const currentAppt = sorted[i];
        let overlapsWithGroup = false;
        
        // Check if current appointment overlaps with any in the current group
        for (const groupAppt of currentGroup) {
          if (isOverlapping(currentAppt, groupAppt)) {
            overlapsWithGroup = true;
            break;
          }
        }
        
        if (overlapsWithGroup) {
          // Add to current group if overlapping
          currentGroup.push(currentAppt);
        } else {
          // Start a new group if not overlapping
          groups.push([...currentGroup]);
          currentGroup = [currentAppt];
        }
      }
      
      // Add the last group
      if (currentGroup.length) {
        groups.push(currentGroup);
      }
      
      return groups;
    };

    // Helper to calculate time range for a professional's bookings
    const calculateTimeRange = (bookings) => {
      if (!bookings || bookings.length === 0) return { start: 8, end: 9 }; // Default 1 hour range
      
      // Find earliest start time and latest end time
      let earliestHour = 24;
      let latestHour = 0;
      
      bookings.forEach(booking => {
        if (!booking.datetimeAtendimento) return;
        
        // Get start time
        const startTime = booking.datetimeAtendimento.split('T')[1];
        const hour = parseInt(startTime.slice(0, 2));
        const minute = parseInt(startTime.slice(3, 5));
        
        // Calculate end time
        const durationMinutes = booking.duration || 30; // Default to 30 if not specified
        const endHour = hour + Math.floor(durationMinutes / 60);
        const endMinute = minute + (durationMinutes % 60);
        const adjustedEndHour = endHour + (endMinute >= 60 ? 1 : 0);
        
        // Update earliest and latest
        earliestHour = Math.min(earliestHour, hour);
        latestHour = Math.max(latestHour, adjustedEndHour);
      });
      
      // Add padding (1 hour before and after)
      earliestHour = Math.max(0, earliestHour - 1);
      latestHour = Math.min(24, latestHour + 1);
      
      // Ensure we have at least a 2 hour window to make the display useful
      if (latestHour - earliestHour < 2) {
        latestHour = earliestHour + 2;
      }
      
      // Make sure latestHour is at least early hour + the duration of the longest appointment + 1
      const longestDuration = Math.max(...bookings.map(b => b.duration || 30)) / 60;
      latestHour = Math.max(latestHour, earliestHour + longestDuration + 1);
      
      // Clamp to valid hours (0-24)
      latestHour = Math.min(24, Math.ceil(latestHour));
      
      return { start: earliestHour, end: latestHour };
    };

    return (
      <div className="calendar-days">
        {dates.map(date => (
          <div key={date} className="calendar-day">
            <h4 className="calendar-date-header">{date}</h4>
            <div className="calendar-professionals">
              {Object.keys(bookingsByDate[date]).map(professionalId => {
                const professional = professionals.find(p => p.id === professionalId);
                const professionalName = professional ? professional.name : professionalId;
                
                // Group overlapping appointments
                const appointmentGroups = groupOverlappingAppointments(bookingsByDate[date][professionalId]);
                
                // Calculate time range for this professional's calendar
                const timeRange = calculateTimeRange(bookingsByDate[date][professionalId]);
                const totalHours = timeRange.end - timeRange.start;
                
                return (
                  <div key={professionalId} className="calendar-professional">
                    <h5 className="professional-name">{professionalName}</h5>
                    <div className="time-grid-container">
                      <div className="time-markers">
                        {Array.from({length: totalHours + 1}, (_, i) => i + timeRange.start).map(hour => (
                          <div 
                            key={hour} 
                            className="time-marker"
                            style={{ top: `${(hour - timeRange.start) * 100 / totalHours}%` }}
                          >
                            {hour}:00
                          </div>
                        ))}
                      </div>
                      <div className="appointment-grid" style={{ 
                        backgroundSize: `100% ${100 / totalHours}%`,
                        '--total-hours': totalHours
                      }}>
                        {appointmentGroups.map((group, groupIndex) => (
                          group.map((booking, bookingIndex) => {
                            // Calculate position and height based on time and duration
                            const startTime = booking.datetimeAtendimento.split('T')[1];
                            const hour = parseInt(startTime.slice(0, 2));
                            const minute = parseInt(startTime.slice(3, 5));
                            
                            // Position from top (in %)
                            const startPosition = ((hour - timeRange.start) * 60 + minute) / (totalHours * 60) * 100;
                            
                            // Height based on duration (in %)
                            const height = (booking.duration / (totalHours * 60)) * 100;
                            
                            // Status colors
                            const bgColor = booking.status === 'Realizado' ? '#2e7d32' : 
                                           booking.status === 'Chegou' ? '#1976d2' :
                                           booking.status === 'Cancelado' ? '#d32f2f' : '#7986cb';
                            
                            // Calculate width and left position based on group size
                            const width = group.length > 1 ? `${100 / group.length}%` : 'calc(100% - 10px)';
                            const left = group.length > 1 ? `${(bookingIndex * 100) / group.length}%` : '5px';
                            
                            return (
                              <div 
                                key={booking.atendimento_id || `${groupIndex}-${bookingIndex}`}
                                className="appointment-block"
                                style={{
                                  top: `${startPosition}%`,
                                  height: `${height}%`,
                                  backgroundColor: bgColor,
                                  width,
                                  left,
                                  cursor: isProfissional ? 'default' : 'pointer',
                                }}
                                onClick={!isProfissional ? () => handleEditBooking(booking) : undefined}
                              >
                                <div className="appointment-content">
                                  <strong>
                                    {booking.paciente} 
                                    {booking.procedimentos && booking.procedimentos.length > 0 && 
                                      ` - ${booking.procedimentos.map(p => p.nome).join(', ')}`
                                    }
                                  </strong>
                                </div>
                              </div>
                            );
                          })
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      <div className="bookings-search-container">
        {/* <div className="google-calendar-banner dark">
          <p>Também é possível ver os agendamentos no Calendário Google</p>
          <a href="https://calendar.google.com/calendar/u/0?cid=OWU0NmQ5NmEwMzZmMmE1MzU5NTc1MzUwNmI5ZmNhMWY2YjU5NzNkNDZmODVjZGVjZDdmMThkM2E1NTA4NGFjZEBncm91cC5jYWxlbmRhci5nb29nbGUuY29t" target="_blank" rel="noopener noreferrer" className="google-calendar-button">
            Abrir Google Calendar
          </a>
        </div> */}
        <div className="bookings-search-filters">
          <div className="search-professional">
            <label>Profissional</label>
            <div className="booking-filter-control">
              <FaUserMd />
              <select 
                value={searchProfessional?.name || ''} 
                onChange={(e) => {
                  const selected = professionals.find(p => p.name === e.target.value);
                  setSearchProfessional(selected || null);
                }}
              >
                <option value="">Todos</option>
                {professionals.map(prof => (
                  <option key={prof.name} value={prof.name}>
                    {prof.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="search-patient">
            <label>Paciente</label>
            <div className="booking-filter-control patient-control">
              <FaSearch />
              <PacientSearchSelect 
                onPatientSelect={(patient) => setSearchPatient(patient)}
                placeholder="Buscar Paciente"
              />
            </div>
          </div>

          <div className="search-date">
            <label>Data</label>
            <div 
              className="booking-filter-control date-input-container"
              onClick={() => document.querySelector('.search-date input[type="date"]')?.showPicker?.()}
            >
              <FaCalendarAlt />
              <input 
                type="date" 
                value={searchDate} 
                onChange={(e) => setSearchDate(e.target.value)}
                className="date-input"
              />
            </div>
          </div>

          <div className="search-status">
            <label>Status</label>
            <div className="status-multiselect" ref={statusDropdownRef}>
              <button
                type="button"
                className={`status-trigger ${isStatusDropdownOpen ? 'open' : ''}`}
                onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
              >
                <span className="status-trigger-content">
                  <FaList />
                  <span>
                    {selectedStatuses.length === statusOptions.length || selectedStatuses.length === 0
                      ? 'Todos'
                      : selectedStatuses.join(', ')}
                  </span>
                </span>
                <FaChevronDown className="status-chevron" />
              </button>
              {isStatusDropdownOpen && (
                <div className="status-dropdown">
                  {statusOptions.map((status) => (
                    <label key={status} className="status-option">
                      <input
                        type="checkbox"
                        checked={selectedStatuses.includes(status)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedStatuses([...selectedStatuses, status]);
                          } else {
                            setSelectedStatuses(selectedStatuses.filter((s) => s !== status));
                          }
                        }}
                      />
                      <span>{status}</span>
                    </label>
                  ))}
                  <div className="status-actions">
                    <button type="button" className="status-clear" onClick={() => setSelectedStatuses([])}>Limpar</button>
                    <button type="button" className="status-all" onClick={() => setSelectedStatuses(statusOptions)}>Todos</button>
                  </div>
                </div>
              )}
            </div>
          </div>

          
        </div>
      </div>
      {bookingsLoading ? (
        <div className="loading-message">Carregando agendamentos...</div>
      ) : (
        renderBookingsList()
      )}
      {renderSaveFeedback()}
      <EditBookingModal 
        showModal={showEditModal && !isProfissional}
        formData={editFormData}
        onClose={() => setShowEditModal(false)}
        onUpdate={setEditFormData}
        onUpdateStatus={handleUpdateBooking}
        isLoading={isLoading}
        professionals={professionals}
        selectedBooking={selectedBooking}
      />
    </>
  );
}

export default ViewBookings; 
